/**
 * Contextual status-line generator for the "Line is thinking" indicator.
 *
 * The old panel showed a single opaque line ("I am thinking through the
 * structure before I build anything.") for the entire duration of a
 * model call. Premium products (Cursor, Perplexity, Notion AI) name the
 * work in plain English and cycle through 2–4 short status lines so the
 * wait feels intentional. This helper picks the sequence from the user's
 * prompt + input signals so what the user sees actually maps to what
 * Line is doing.
 */

export interface LineStatusInput {
  userText: string
  isEdit: boolean
  hasImages: boolean
  hasAttachments: boolean
}

const HAS_PLAN_VERB = /\b(plan|prepare|make|build|create|draft|organize|schedule|design|structure|arrange|coordinate|sketch|outline|help\s+me)\b/i
const HAS_DATE = /\b(\d+)[-\s]+(day|week|month|year)s?\b|\b(today|tomorrow|next\s+(sun|mon|tue|wed|thu|fri|sat)|by\s+\w+)\b|\b\d{4}-\d{2}-\d{2}\b/i
const HAS_RESOURCES = /\b(I\s+have|available|ingredients|resources|budget|inventory)\b/i
const HAS_PEOPLE = /\bfor\s+(my|the|our|client|customer)\b|\b(brother|sister|mom|dad|husband|wife|partner|team|client|customer)\b/i

/**
 * Build a rotating sequence of 3-4 short status lines.
 * Each line is a verb-first, present-continuous fragment; no filler.
 */
export const buildLineThinkingStatuses = ({
  userText,
  isEdit,
  hasImages,
  hasAttachments,
}: LineStatusInput): string[] => {
  const text = (userText ?? '').trim()

  if (isEdit) {
    return [
      'Reading the selected scope…',
      'Checking date containment…',
      'Shaping the edit…',
    ]
  }

  if (hasImages) {
    return [
      'Looking through the image…',
      'Turning what I see into a plan…',
      'Sketching the timeline…',
    ]
  }

  if (hasAttachments) {
    return [
      'Reading your attached context…',
      'Extracting the useful bits…',
      'Sketching a plan around it…',
    ]
  }

  const sequence: string[] = ['Pulling your active plans…']
  if (HAS_RESOURCES.test(text)) sequence.push('Cataloguing what you have on hand…')
  if (HAS_PEOPLE.test(text)) sequence.push('Framing this for the people involved…')
  if (HAS_DATE.test(text)) sequence.push('Backwards-planning from your deadline…')
  if (HAS_PLAN_VERB.test(text)) sequence.push('Roughing out phases…')
  sequence.push('Sketching the timeline…')
  sequence.push('Tightening the checklist…')

  return Array.from(new Set(sequence)).slice(0, 4)
}
